import React, { MouseEvent, useCallback } from "react";
import { connect } from "react-redux";

import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { useIntl } from "react-intl";

import DealLossReason from "@mapmycustomers/shared/types/entity/deals/DealLossReason";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Popconfirm } from "@mapmycustomers/ui";

import { deleteDealLossReason } from "@app/store/dealLossReasons/actions";

import styles from "./DeleteLossReasonButton.module.scss";

interface Props {
  className?: string;
  deleteDealLossReason: typeof deleteDealLossReason.request;
  lossReason: DealLossReason;
}

const DeleteLossReasonButton = ({ className, deleteDealLossReason, lossReason }: Props) => {
  const intl = useIntl();
  const [deleteConfirmationVisible, showDeleteConfirmation, hideDeleteConfirmation] = useBoolean();

  const handleDeleteReasonClick = useCallback(
    (e?: MouseEvent) => {
      stopEvents(e);
      hideDeleteConfirmation();
      deleteDealLossReason(lossReason.id);
    },
    [deleteDealLossReason, hideDeleteConfirmation, lossReason]
  );

  const handleCancelClick = useCallback(
    (e?: MouseEvent) => {
      stopEvents(e);
      hideDeleteConfirmation();
    },
    [hideDeleteConfirmation]
  );

  const handleIconClick = useCallback(
    (e: MouseEvent) => {
      stopEvents(e);
      showDeleteConfirmation();
    },
    [showDeleteConfirmation]
  );

  return (
    <Popconfirm
      cancelText={intl.formatMessage({
        id: "dealLostModal.deleteConfirmation.cancel",
        defaultMessage: "Cancel",
        description: "Cancel button in delete loss reason confirmation",
      })}
      className={cn(styles.container, className)}
      description={intl.formatMessage({
        id: "dealLostModal.delete.confirmation.description",
        defaultMessage: "You will not be able to recover this if deleted.",
        description: "Text in delete loss reason confirmation description",
      })}
      destroyTooltipOnHide
      okText={intl.formatMessage({
        id: "dealLostModal.deleteConfirmation.ok",
        defaultMessage: "Delete",
        description: "Ok button in delete loss reason confirmation",
      })}
      onCancel={handleCancelClick}
      onConfirm={handleDeleteReasonClick}
      open={deleteConfirmationVisible}
      overlayClassName={styles.overlay}
      placement="right"
      title={intl.formatMessage({
        id: "dealLostModal.delete.confirmation.title",
        defaultMessage: "Are you sure to delete this reason?",
        description: "Text in delete loss reason confirmation title",
      })}
    >
      <FontAwesomeIcon
        className={styles.deleteIcon}
        icon={faMinusCircle}
        onClick={handleIconClick}
      />
    </Popconfirm>
  );
};

const mapDispatchToProps = {
  deleteDealLossReason: deleteDealLossReason.request,
};

export default connect(null, mapDispatchToProps)(DeleteLossReasonButton);
